// Search cache service for AI cost optimization
// Implements multi-layer caching to reduce AI API calls by up to 90%
import { storageService } from "./storageService";
import { waitForTransaction } from "./indexedDbTx";

interface CachedSearch {
  id: string;
  userId: string;
  queryHash: string;
  queryText: string;
  tags: string[];
  resultIds: string[];
  cachedAt: number;
  expiresAt: number;
}

interface CachedTags {
  id: string;
  queryHash: string;
  queryText: string;
  tags: string[];
  cachedAt: number;
  expiresAt: number;
}

interface CacheStats {
  searchCacheHits: number;
  tagCacheHits: number;
  totalSearches: number;
}

const STORE_SEARCH_CACHE = "searchCache";
const STORE_TAG_CACHE = "tagCache";
const STORE_CACHE_STATS = "cacheStats";

// Query normalization to maximize cache hits
export function normalizeQuery(query: string): string {
  return query
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ") // Multiple spaces → single space
    .replace(/[^\w\s]/g, "") // Remove punctuation
    .replace(/\b(photos?|pictures?|images?|pics?)\b/g, "") // Remove common words
    .trim();
}

// Generate consistent hash for cache keys
export async function hashQuery(query: string): Promise<string> {
  const normalized = normalizeQuery(query);
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function openDB(): Promise<IDBDatabase> {
  return storageService.openDB();
}

export const searchCacheService = {
  /**
   * Layer 1: Get cached full search result
   */
  async getCachedSearch(
    userId: string,
    query: string,
  ): Promise<{ tags: string[]; resultIds: string[] } | null> {
    try {
      const db = await openDB();
      const queryHash = await hashQuery(query);

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_SEARCH_CACHE, "readonly");
        const index = tx.objectStore(STORE_SEARCH_CACHE).index("userQuery");
        const request = index.get([userId, queryHash]);

        request.onsuccess = () => {
          const cached = request.result as CachedSearch | undefined;

          if (cached && cached.expiresAt > Date.now()) {
            // Update stats
            this.incrementCacheHit(userId, "search").catch(() => {});
            resolve({
              tags: cached.tags,
              resultIds: cached.resultIds,
            });
          } else {
            resolve(null);
          }
        };

        request.onerror = () => resolve(null);
      });
    } catch (error) {
      console.warn("Cache lookup failed:", error);
      return null;
    }
  },

  /**
   * Layer 1: Save cached full search result
   */
  async saveCachedSearch(
    userId: string,
    query: string,
    tags: string[],
    resultIds: string[],
  ): Promise<void> {
    try {
      const db = await openDB();
      const queryHash = await hashQuery(query);
      const now = Date.now();
      const expiresAt = now + 7 * 24 * 60 * 60 * 1000; // 7 days

      const cached: CachedSearch = {
        id: `${userId}-${queryHash}`,
        userId,
        queryHash,
        queryText: query,
        tags,
        resultIds,
        cachedAt: now,
        expiresAt,
      };

      const tx = db.transaction(STORE_SEARCH_CACHE, "readwrite");
      await new Promise<void>((resolve, reject) => {
        const request = tx.objectStore(STORE_SEARCH_CACHE).put(cached);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await waitForTransaction(tx);
    } catch (error) {
      console.warn("Failed to save search cache:", error);
    }
  },

  /**
   * Layer 2: Get cached tag generation
   */
  async getCachedTags(query: string): Promise<string[] | null> {
    try {
      const db = await openDB();
      const queryHash = await hashQuery(query);

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_TAG_CACHE, "readonly");
        const index = tx.objectStore(STORE_TAG_CACHE).index("queryHash");
        const request = index.get(queryHash);

        request.onsuccess = () => {
          const cached = request.result as CachedTags | undefined;

          if (cached && cached.expiresAt > Date.now()) {
            resolve(cached.tags);
          } else {
            resolve(null);
          }
        };

        request.onerror = () => resolve(null);
      });
    } catch (error) {
      console.warn("Tag cache lookup failed:", error);
      return null;
    }
  },

  /**
   * Layer 2: Save cached tag generation
   */
  async saveCachedTags(query: string, tags: string[]): Promise<void> {
    try {
      const db = await openDB();
      const queryHash = await hashQuery(query);
      const now = Date.now();
      const expiresAt = now + 30 * 24 * 60 * 60 * 1000; // 30 days

      const cached: CachedTags = {
        id: queryHash,
        queryHash,
        queryText: query,
        tags,
        cachedAt: now,
        expiresAt,
      };

      const tx = db.transaction(STORE_TAG_CACHE, "readwrite");
      await new Promise<void>((resolve, reject) => {
        const request = tx.objectStore(STORE_TAG_CACHE).put(cached);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await waitForTransaction(tx);
    } catch (error) {
      console.warn("Failed to save tag cache:", error);
    }
  },

  /**
   * Delete cached search (for invalidation)
   */
  async deleteCachedSearch(userId: string, queryHash: string): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_SEARCH_CACHE, "readwrite");
      const index = tx.objectStore(STORE_SEARCH_CACHE).index("userQuery");
      const request = index.getKey([userId, queryHash]);

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => {
          if (request.result) {
            const deleteReq = tx
              .objectStore(STORE_SEARCH_CACHE)
              .delete(request.result);
            deleteReq.onsuccess = () => resolve();
            deleteReq.onerror = () => reject(deleteReq.error);
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });

      await waitForTransaction(tx);
    } catch (error) {
      console.warn("Failed to delete cached search:", error);
    }
  },

  /**
   * Cleanup expired cache entries
   */
  async cleanupExpired(): Promise<void> {
    try {
      const db = await openDB();
      const now = Date.now();

      // Cleanup search cache
      const searchTx = db.transaction(STORE_SEARCH_CACHE, "readwrite");
      const searchIndex = searchTx
        .objectStore(STORE_SEARCH_CACHE)
        .index("expiresAt");
      const searchRange = IDBKeyRange.upperBound(now);
      const searchRequest = searchIndex.openCursor(searchRange);

      await new Promise<void>((resolve) => {
        searchRequest.onsuccess = (e: any) => {
          const cursor = e.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
        searchRequest.onerror = () => resolve();
      });

      await waitForTransaction(searchTx);

      // Cleanup tag cache
      const tagTx = db.transaction(STORE_TAG_CACHE, "readwrite");
      const tagIndex = tagTx.objectStore(STORE_TAG_CACHE).index("expiresAt");
      const tagRange = IDBKeyRange.upperBound(now);
      const tagRequest = tagIndex.openCursor(tagRange);

      await new Promise<void>((resolve) => {
        tagRequest.onsuccess = (e: any) => {
          const cursor = e.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
        tagRequest.onerror = () => resolve();
      });

      await waitForTransaction(tagTx);
    } catch (error) {
      console.warn("Cache cleanup failed:", error);
    }
  },

  /**
   * Get cache statistics
   */
  async getCacheStats(userId: string): Promise<CacheStats> {
    try {
      const db = await openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(STORE_CACHE_STATS, "readonly");
        const request = tx.objectStore(STORE_CACHE_STATS).get(userId);

        request.onsuccess = () => {
          const stats = request.result as CacheStats | undefined;
          resolve(
            stats || {
              searchCacheHits: 0,
              tagCacheHits: 0,
              totalSearches: 0,
            },
          );
        };

        request.onerror = () => {
          resolve({
            searchCacheHits: 0,
            tagCacheHits: 0,
            totalSearches: 0,
          });
        };
      });
    } catch (error) {
      return {
        searchCacheHits: 0,
        tagCacheHits: 0,
        totalSearches: 0,
      };
    }
  },

  /**
   * Increment cache hit counter
   */
  async incrementCacheHit(
    userId: string,
    type: "search" | "tag",
  ): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_CACHE_STATS, "readwrite");
      const store = tx.objectStore(STORE_CACHE_STATS);

      const request = store.get(userId);
      await new Promise<void>((resolve) => {
        request.onsuccess = () => {
          const stats = request.result as CacheStats | undefined;
          const updated: CacheStats = {
            searchCacheHits:
              (stats?.searchCacheHits || 0) + (type === "search" ? 1 : 0),
            tagCacheHits: (stats?.tagCacheHits || 0) + (type === "tag" ? 1 : 0),
            totalSearches: (stats?.totalSearches || 0) + 1,
          };

          const putRequest = store.put({ userId, ...updated });
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => resolve();
        };
        request.onerror = () => resolve();
      });

      await waitForTransaction(tx);
    } catch (error) {
      // Silently fail
    }
  },

  /**
   * Clear all cache for a user
   */
  async clearUserCache(userId: string): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_SEARCH_CACHE, "readwrite");
      const index = tx.objectStore(STORE_SEARCH_CACHE).index("userId");
      const request = index.openCursor(IDBKeyRange.only(userId));

      await new Promise<void>((resolve) => {
        request.onsuccess = (e: any) => {
          const cursor = e.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => resolve();
      });

      await waitForTransaction(tx);
    } catch (error) {
      console.warn("Failed to clear user cache:", error);
    }
  },
};

// Run cleanup on service load
if (typeof window !== "undefined") {
  // Cleanup expired entries every hour
  setInterval(
    () => {
      searchCacheService.cleanupExpired().catch(() => {});
    },
    60 * 60 * 1000,
  );

  // Initial cleanup
  searchCacheService.cleanupExpired().catch(() => {});
}
